import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { forwardRef, Fragment, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import Typography from '@mui/material/Typography';
import { FormContainer, FormErrorProvider, RadioButtonGroup, SelectElement, useForm, useWatch } from "react-hook-form-mui";
import { useFlow } from "../../context/FlowContext";
import { Link } from "react-router-dom";
import { CustomCheckboxElement, CustomDatePickerElement, CustomMultiSelectElement, CustomTextareaAutosizeElement, CustomTextFieldElement, CustomTimePickerElement } from "../../layouts/Theme";
import useWidgetStore from "../../store/useWidgetStore";
import { useI18N } from "../../context/I18NContext";
import { Alert, Box, Button, Divider, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, useTheme } from "@mui/material";
import { useSettings } from "../../context/SettingsContext";
import PasswordField from "../fields/PasswordField";
import BottomBar from "../BottomBar";
import { useSetConsumer } from "../../hooks/query/useSetConsumer";
import CountrySelectField from "../fields/CountrySelectField";
import BirthdayField from "../fields/BirthdayField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { enUS, nlNL, frFR, deDE } from "@mui/x-date-pickers/locales";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import UploadField from "../fields/UploadField";
const RegisterForm = forwardRef(function RegisterForm({ bookingCore, mode, registerRequired, disableSubmit, callback, scrollErrorsIntoView }, ref) {
    var _a;
    const { t, ts } = useI18N();
    const theme = useTheme();
    const formContext = useForm({});
    const flow = useFlow();
    const errorsId = useId();
    const formRef = useRef(null);
    // every route this form is the whole screen of: the submit belongs in the bar at the bottom
    // of the widget, the same place every other step of the flow has it. 'accountDetails1' is
    // the account screen and 'accountDetails' the flow step, but they are the same form.
    const stickySubmit = ["register", "accountDetails", "accountDetails1", "summary"].includes(mode);
    // Where the submit is pinned to the bottom it stays in sight while the field that failed
    // validation can be well above the fold, so the form would look like it simply refuses to
    // advance. The summary is excluded: its errors come back from the server, and the alert at
    // the top of the form already handles those.
    const scrollErrors = ["register", "accountDetails", "accountDetails1"].includes(mode);
    const [localeText, setLocaleText] = useState({});
    const { consumer, language, isGuest, isAccount, setConsumer, setIsGuest, setIsAccount, setIsLoginAccount, setIsNewAccount, getSourceHelpers, isLoginAccount } = useWidgetStore();
    const { forms, contracts, hasAccounts, api_key } = useSettings();
    const consumerQuery = useSetConsumer();
    const [error, setError] = useState();
    const [rawError, setRawError] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [missingContracts, setMissingContracts] = useState(false);
    const [showRemark, setShowRemark] = useState(false);
    useEffect(() => {
        if (language === 'nl')
            setLocaleText(nlNL.components.MuiLocalizationProvider.defaultProps.localeText);
        if (language === 'en')
            setLocaleText(enUS.components.MuiLocalizationProvider.defaultProps.localeText);
        if (language === 'fr')
            setLocaleText(frFR.components.MuiLocalizationProvider.defaultProps.localeText);
        if (language === 'de')
            setLocaleText(deDE.components.MuiLocalizationProvider.defaultProps.localeText);
    }, [language]);
    useEffect(() => {
        // After we get a hash above, we can start the next request
        // If not, an error has been shown that the customer API call failed
        if (mode === "register" && (consumer === null || consumer === void 0 ? void 0 : consumer.hash)) {
            if (flow.onNext) {
                flow.onNext();
            }
        }
    }, [consumer === null || consumer === void 0 ? void 0 : consumer.lastUpdate]);
    useEffect(() => {
        var _a, _b, _c, _d;
        if ((_a = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _a === void 0 ? void 0 : _a.config) {
            setShowRemark((_d = (_c = (_b = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _b === void 0 ? void 0 : _b.config) === null || _c === void 0 ? void 0 : _c.showRemark) !== null && _d !== void 0 ? _d : true);
        }
    }, [flow]);
    const type = useWatch({ control: formContext.control, name: "type" });
    const externalFormSubmit = () => {
        formContext.handleSubmit(onSubmit, onError)();
    };
    // expose externalFormSubmit to a parent through the ref, so the booking flow
    // can trigger this form's submit from a button rendered outside the form.
    useImperativeHandle(ref, () => ({ externalFormSubmit }));
    const onSubmit = (data, event) => {
        var _a;
        let _consumer = consumer, _business = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.business) !== null && _a !== void 0 ? _a : {
            address: "",
            zipCode: "",
            city: "",
            name: "",
            vatNumber: ""
        }, _contracts = [], _fields = {};
        for (const [key, value] of Object.entries(data)) {
            if (value === undefined)
                continue;
            if (key.startsWith('business_')) {
                // note: because the form field names are dotted, like business.name, they are already collected
                // together in the business entry as an object
                // we still have the underscored versions, like business_name, for technical reasons in the form
            }
            else if (key === 'business') {
                _business = value;
            }
            else if (key.startsWith('custom')) {
                // @ts-ignore
                _fields[key] = value;
            }
            else {
                // handle some garbage in/ garbage out
                // @ts-ignore
                _consumer[key] = value !== null && value !== 'undefined' && value !== 'null' ? value : '';
            }
        }
        contracts.map((contract, index) => {
            if (data[contract.name]) {
                _contracts.push(contract.name);
            }
        });
        if (data.password !== '') {
            _consumer.username = data.username;
        }
        if (data.street) {
            _consumer.address = data.street;
        }
        _consumer.business = _business;
        _consumer.contracts = _contracts;
        _consumer.fields = _fields;
        setConsumer(_consumer);
        setSubmitted(true);
        consumerQuery.refetch();
    };
    const onError = (errors) => {
        scrollToFirstError();
    };
    const scrollToFirstError = () => {
        if (bookingCore || !scrollErrors)
            return;
        // after the paint that renders the error state, and after react-hook-form has had its
        // own go at focusing the first invalid input
        requestAnimationFrame(() => {
            var _a, _b;
            const invalid = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.Mui-error, [aria-invalid="true"]');
            if (!invalid)
                return;
            const field = (_b = invalid.closest('.MuiFormControl-root, .MuiFormGroup-root, .MuiFormControlLabel-root')) !== null && _b !== void 0 ? _b : invalid;
            field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    };
    useEffect(() => {
        var _a, _b;
        // consumerQuery is useSetConsumer(), a useQuery keyed on the single literal
        // ['widget-setCustomer'] - shared by every RegisterForm/SigninForm/Mandate instance in the
        // whole widget, not scoped per instance or per mount. A freshly mounted RegisterForm (e.g.
        // this same component, later, in accountDetails1 mode) can see .data already populated
        // from a completely different, earlier instance's successful submit (registering, earlier
        // in the flow) before this instance ever called refetch() itself - `submitted` is this
        // instance's own "I actually just asked for this" flag, set immediately before its own
        // refetch() call below, and is what tells stale shared-cache data apart from a real answer
        // to THIS submission. Without it, mounting on stale cached data silently replayed whatever
        // the earlier submit did - for accountDetails1 that meant instantly calling
        // flow.returnToFlow() and bouncing back to the first view before the consumer touched
        // anything.
        if (consumerQuery.data && submitted) {
            const statusObj = consumerQuery.data.status || {};
            if (statusObj.status === 'failed') {
                let _errors = [
                //  'Klantaccount kan niet worden aangemaakt'
                ], _rawErrors = [];
                if (statusObj.userMessages) {
                    for (const [_key, _error] of Object.entries(statusObj.userMessages)) {
                        let error = _error;
                        _rawErrors.push(_key);
                        if (error.includes('#oa_reminder')) {
                            let link = t(error).match(/<a (.+?)>(.+?)<\/a>/);
                            if (link) {
                                let parts = error.toString().split(link[0]);
                                error = (_jsxs(_Fragment, { children: [_jsx("span", { children: parts[0] }), _jsx(Link, { to: "/oa_widget_passwordReminder1", children: link[2] }), _jsx("span", { children: parts[1] })] }));
                            }
                        }
                        else {
                            // make it an object, so the server-side error message are never translated and are assumed
                            // already translated. The error list in the renderer checks if the error is a string or not
                            error = (_jsx(_Fragment, { children: error }));
                        }
                        _errors.push(error);
                        if (_key === 'password') {
                            formContext.setError(_key, { type: 'custom', message: _error });
                        }
                    }
                }
                else {
                    _errors.push('Er is een onbekende fout opgetreden tijdens het opslaan');
                }
                setError(_errors);
                setRawError(_rawErrors);
                (_b = (_a = document === null || document === void 0 ? void 0 : document.getElementById("register-form")) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth"
                });
            }
            else {
                // save the hash in Consumer object state...
                setConsumer(Object.assign(Object.assign({}, consumer), { lastUpdate: consumerQuery.data.status.timestamp, id: consumerQuery.data.result.item.id, hash: consumerQuery.data.result.item.hash, companyId: consumerQuery.data.result.item.companyId, provisional: consumerQuery.data.result.item.provisional, 
                    /// if we got here without an error, and we have a username, we have an account
                    // the API still returns false, but following calls return true
                    hasUserAccount: !!consumer.username //consumerQuery.data.result.item.hasUserAccount
                 }));
                // The consumer now exists server-side, so the four account flags have to leave
                // the "anonymous visitor" state together. Leaving isGuest true here is what let
                // the flow keep signIn/register valid and drop accountDetails, because
                // FlowContext.checkRequirement reads one flag per requirement.
                setIsGuest(false);
                setIsAccount(true);
                if (bookingCore) {
                    if (callback && submitted) {
                        setSubmitted(false);
                        callback({
                            remark: formContext.getValues('remark'),
                            fields: formContext.getValues('fields')
                        });
                    }
                }
                else {
                    if (mode === "register") {
                        setIsNewAccount(true);
                        setIsLoginAccount(false);
                        // the onNext is handled by watching the consumer.hash
                    }
                    else if (mode === "accountDetails1") {
                        // the account screen is outside the flow, so there is no previous step to
                        // walk back to: saving returns to wherever the menu was opened from
                        if (flow.returnToFlow) {
                            flow.returnToFlow();
                        }
                    }
                    else if (mode === "accountDetails") {
                        if (flow.onNext) {
                            flow.onNext();
                        }
                    }
                    else if (mode === "summary") {
                        if (callback && submitted) {
                            setSubmitted(false);
                            callback({
                                remark: formContext.getValues('remark'),
                                fields: formContext.getValues('fields')
                            });
                        }
                    }
                    else {
                        console.log(' unhandled case?');
                    }
                }
            }
        }
    }, [consumerQuery.data]);
    useEffect(() => {
        var _a, _b;
        // After a login, SigninForm re-posts the consumer through the shared setCustomer query. When the
        // consumer has not accepted the latest terms/privacy statement that fails, and this form (mounted
        // afterwards, so with `submitted` still false) is the only place they can still accept them.
        const messages = ((_b = (_a = consumerQuery.data) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.userMessages) || {}, errorAttributes = Object.keys(messages);
        if (errorAttributes.includes('acceptPrivacy') || errorAttributes.includes('acceptTerms')) {
            setMissingContracts(true);
        }
    }, [consumerQuery.data]);
    useEffect(() => {
        if (!submitted && error) {
            // these errors were generated by the login procedure
            setError(undefined);
            setMissingContracts(rawError && rawError.filter((error) => ['acceptTerms', 'acceptPrivacy'].includes(error)).length > 0);
        }
    }, [error]);
    // scroll the error list into view once, on its first appearance — not on
    // later error changes while it stays visible (reset when it clears).
    const scrolledToErrors = useRef(false);
    useEffect(() => {
        var _a;
        if (!error) {
            scrolledToErrors.current = false;
            return;
        }
        if (scrollErrorsIntoView && !scrolledToErrors.current) {
            scrolledToErrors.current = true;
            (_a = document.getElementById(errorsId)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [error]);
    const loadValues = () => {
        if (formContext && consumer && forms) {
            for (const [key, value] of Object.entries(consumer)) {
                if (value) {
                    formContext.setValue(key, value);
                }
                if (key === 'business') {
                    for (const [business_key, business_value] of Object.entries(value)) {
                        formContext.setValue(`business_${business_key}`, business_value);
                    }
                }
                else if (key === 'contracts') {
                    for (const [contract_key, contract_name] of Object.entries(value)) {
                        formContext.setValue(contract_name, true);
                        // todo: figure out how to check the already accepted terms after a login
                        // if(contract_name === 'acceptPrivacy') {
                        //     formContext.setValue(contract_name as any, 1)
                        // }
                        // else {
                        //     formContext.setValue(contract_name as any, 2)
                        // }
                    }
                }
                else if (key === 'fields') {
                    for (const [fields_key, fields_value] of Object.entries(value)) {
                        formContext.setValue(fields_key, fields_value);
                    }
                }
            }
        }
    };
    useEffect(() => {
        var _a;
        loadValues();
        if (forms.business && !forms.consumer) {
            // if we only have a business forms configured, select it
            formContext.setValue('type', 'business');
        }
        else {
            // otherwise, select the correct form for the current consumer, defaulting to the consumer form
            formContext.setValue('type', (_a = consumer === null || consumer === void 0 ? void 0 : consumer.type) !== null && _a !== void 0 ? _a : 'consumer');
        }
    }, [consumer, formContext, forms]);
    const renderField = (field, name) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9;
        //name = `${type}_${name}`
        switch (field.type) {
            case 'dob':
                return (_jsx(_Fragment, { children: _jsx(BirthdayField, { name: name, field: field }) }));
            case 'country':
                return (_jsx(_Fragment, { children: _jsx(CountrySelectField, { name: name, field: field }) }));
            case 'textarea':
                return (_jsx(CustomTextareaAutosizeElement, { required: (_a = field.required) !== null && _a !== void 0 ? _a : false, name: name, label: t(field.label, 'register'), rows: (_c = (_b = field === null || field === void 0 ? void 0 : field.config) === null || _b === void 0 ? void 0 : _b.rows) !== null && _c !== void 0 ? _c : 3, sx: { my: 1 }, fullWidth: true, validation: { maxLength: (_e = (_d = field === null || field === void 0 ? void 0 : field.config) === null || _d === void 0 ? void 0 : _d.maxLength) !== null && _e !== void 0 ? _e : 65536 } }));
            case 'select':
                let _options = ((_g = (_f = field === null || field === void 0 ? void 0 : field.config) === null || _f === void 0 ? void 0 : _f.options) !== null && _g !== void 0 ? _g : []).map((option) => {
                    return { id: option.id, label: t(option.label, 'select') };
                });
                return (_jsx(SelectElement, { required: (_h = field.required) !== null && _h !== void 0 ? _h : false, label: t(field.label, 'register'), options: _options, name: name, sx: bookingCore ? {} : {
                        my: 1,
                        borderColor: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.default,
                        "& fieldset": {
                            borderColor: theme.palette.text.secondary
                        },
                        "& .Mui-focused": {
                            borderColor: theme.palette.primary.main,
                            color: `${theme.palette.text.primary} !important`
                        },
                        "& label": {
                            color: theme.palette.text.primary
                        },
                        "& svg": {
                            color: theme.palette.text.secondary
                        },
                        "& .MuiInputBase-formControl": {
                            backgroundColor: theme.palette.background.default,
                        },
                        "& .MuiSelect-select": {
                            padding: "34px 14px 16.5px 14px"
                        },
                        "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
                            transform: "translate(14px, 28px) scale(1)" // translate(14px, 16px) scale(1);
                        },
                        "& .MuiInputLabel-shrink": {
                            transform: "translate(14px, 14px) scale(0.75)" // translate(14px, -9px) scale(0.75);
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            top: 0,
                            "& legend": {
                                display: 'none'
                            }
                        }
                    }, fullWidth: true }));
            case 'multi':
                let _options2 = ((_k = (_j = field === null || field === void 0 ? void 0 : field.config) === null || _j === void 0 ? void 0 : _j.options) !== null && _k !== void 0 ? _k : []).map((option) => {
                    return { id: option.id, label: t(option.label, 'select') };
                });
                if (((_l = field.config) === null || _l === void 0 ? void 0 : _l.minSelected) === 1 && ((_m = field.config) === null || _m === void 0 ? void 0 : _m.maxSelected) === 1) {
                    // this is a radio group
                    return (_jsx(Box, { sx: { my: 1 }, children: _jsx(RadioButtonGroup, { required: (_o = field.required) !== null && _o !== void 0 ? _o : false, label: t(field.label), options: _options2, name: name }) }));
                }
                return (_jsx(CustomMultiSelectElement, { required: (_p = field.required) !== null && _p !== void 0 ? _p : false, label: t(field.label, 'register'), id: 'select-'.concat(name), options: _options2, showCheckbox: true, name: name, sx: bookingCore ? {} : {
                        my: 1,
                        borderColor: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.default,
                        "& fieldset": {
                            borderColor: theme.palette.text.primary
                        },
                        "& .MuiSelect-icon": {
                            color: theme.palette.text.primary
                        },
                        "& .Mui-focused": {
                            borderColor: theme.palette.text.secondary,
                            color: `${theme.palette.text.primary} !important`
                        },
                        "& label.MuiFormLabel-root": {
                            color: theme.palette.text.primary
                        },
                        "& .MuiSelect-multiple": {
                            fontSize: '1.25rem'
                        }
                    }, inputProps: {
                        onChange: (event) => {
                            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
                            // only if a maxSelected is set, we need this range check
                            if (field.config.maxSelected) {
                                let minSelected = (_a = field.config.minSelected) !== null && _a !== void 0 ? _a : (field.required ? 1 : 0), maxSelected = (_b = field.config.maxSelected) !== null && _b !== void 0 ? _b : 9999;
                                if (maxSelected === 1)
                                    return; // this is a radio button group
                                //@ts-ignore
                                let selected = (_e = (_d = (_c = event === null || event === void 0 ? void 0 : event.target) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0;
                                let select = document.getElementById(`select-${name}`);
                                if (select) {
                                    let input = (_f = select.parentNode) === null || _f === void 0 ? void 0 : _f.querySelector('input');
                                    if (input) {
                                        if (selected < minSelected || selected > maxSelected) {
                                            input.pattern = "/^([^,]+?),([^,]+?)$/";
                                            input.setCustomValidity(`select between ${minSelected} and ${maxSelected} options`);
                                            input.reportValidity();
                                            select.classList.add('Mui-error');
                                            (_g = select.parentElement) === null || _g === void 0 ? void 0 : _g.classList.add('Mui-error');
                                            (_k = (_j = (_h = select.parentNode) === null || _h === void 0 ? void 0 : _h.parentNode) === null || _j === void 0 ? void 0 : _j.querySelector('label')) === null || _k === void 0 ? void 0 : _k.classList.add('Mui-error');
                                        }
                                        else {
                                            select.classList.remove('Mui-error');
                                            (_l = select.parentElement) === null || _l === void 0 ? void 0 : _l.classList.remove('Mui-error');
                                            (_p = (_o = (_m = select.parentNode) === null || _m === void 0 ? void 0 : _m.parentNode) === null || _o === void 0 ? void 0 : _o.querySelector('label')) === null || _p === void 0 ? void 0 : _p.classList.remove('Mui-error');
                                        }
                                    }
                                }
                            }
                        }
                    }, MenuProps: {
                        sx: {
                            "& svg": {
                                color: theme.palette.text.secondary,
                            }
                        }
                    }, formControlProps: {
                        sx: bookingCore ? {} : {
                            "& .MuiSelect-select": {
                                padding: "26px 14px 16.5px 14px"
                            },
                            "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
                                transform: "translate(14px, 28px) scale(1)" // translate(14px, 16px) scale(1);
                            },
                            "& .MuiInputLabel-shrink": {
                                color: theme.palette.text.primary,
                                transform: "translate(14px, 14px) scale(0.75)" // translate(14px, -9px) scale(0.75);
                            },
                            "& .MuiSelect-icon": {
                                color: theme.palette.text.secondary
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                top: 0,
                                "& legend": {
                                    display: 'none'
                                }
                            }
                        }
                    }, fullWidth: true }));
            case 'checkbox':
            case 'boolean':
                let label = t(field.label, 'register');
                if (field.required) {
                    label = label.concat('*');
                }
                return (_jsx(Box, { children: _jsx(CustomCheckboxElement, { sx: {
                            marginLeft: 1
                        }, labelProps: {
                            sx: bookingCore ? {} : {
                                color: theme.palette.text.primary,
                                "& a": {
                                    color: theme.palette.text.primary
                                },
                                "& .MuiFormControlLabel-label": {
                                    fontSize: '1.25rem'
                                },
                                "& svg": {
                                    color: theme.palette.text.primary
                                }
                            }
                        }, required: (_q = field.required) !== null && _q !== void 0 ? _q : false, label: label, name: name }) }));
            case 'hidden':
                return (_jsx("input", Object.assign({}, formContext.register(name, {}), { value: (_r = field.config) === null || _r === void 0 ? void 0 : _r.value, type: "hidden" })));
            case 'html.header':
                return (_jsx(Typography, { variant: "h6", sx: bookingCore ? {} : {
                        marginLeft: 1,
                        marginTop: 2,
                        color: theme.palette.text.primary
                    }, children: t(field.label, 'register') }));
            case 'html.paragraph':
                return (_jsx(Typography, { variant: "body1", sx: bookingCore ? {} : {
                        marginLeft: 1,
                        marginTop: 2
                    }, children: t(field.label, 'register') }));
            case 'number':
                return (_jsx(CustomTextFieldElement, { label: t(field.label, 'register'), name: name, type: 'number', required: (_s = field.required) !== null && _s !== void 0 ? _s : false, validation: {
                        min: { value: (_u = (_t = field === null || field === void 0 ? void 0 : field.config) === null || _t === void 0 ? void 0 : _t.minValue) !== null && _u !== void 0 ? _u : 0, message: ts('Minimale waarde is %value%', { value: (_w = (_v = field === null || field === void 0 ? void 0 : field.config) === null || _v === void 0 ? void 0 : _v.minValue) !== null && _w !== void 0 ? _w : 0 }, 'register') },
                        max: { value: (_y = (_x = field === null || field === void 0 ? void 0 : field.config) === null || _x === void 0 ? void 0 : _x.maxValue) !== null && _y !== void 0 ? _y : 100, message: ts('Maximale waarde is %value%', { value: (_0 = (_z = field === null || field === void 0 ? void 0 : field.config) === null || _z === void 0 ? void 0 : _z.maxValue) !== null && _0 !== void 0 ? _0 : 100 }, 'register') },
                    }, fullWidth: true, inputProps: {
                        min: (_2 = (_1 = field === null || field === void 0 ? void 0 : field.config) === null || _1 === void 0 ? void 0 : _1.minValue) !== null && _2 !== void 0 ? _2 : 0,
                        max: (_4 = (_3 = field === null || field === void 0 ? void 0 : field.config) === null || _3 === void 0 ? void 0 : _3.maxValue) !== null && _4 !== void 0 ? _4 : 100,
                        step: (_6 = (_5 = field === null || field === void 0 ? void 0 : field.config) === null || _5 === void 0 ? void 0 : _5.step) !== null && _6 !== void 0 ? _6 : 1
                    } }));
            case 'date':
                return (_jsx(CustomDatePickerElement, { label: t(field.label, 'register'), name: name, required: (_7 = field.required) !== null && _7 !== void 0 ? _7 : false, sx: bookingCore ? {} : {
                        width: '100%',
                        my: 1
                    } }));
            case 'time':
                return (_jsx(CustomTimePickerElement, { label: t(field.label, 'register'), name: name, required: (_8 = field.required) !== null && _8 !== void 0 ? _8 : false, sx: bookingCore ? {} : {
                        width: '100%',
                        my: 1
                    } }));
            case 'file':
                return (_jsx(_Fragment, { children: _jsx(UploadField, { name: name, field: field }) }));
        }
        return (_jsx(CustomTextFieldElement, { label: t(field.label, 'register'), name: name, type: field.type, required: (_9 = field.required) !== null && _9 !== void 0 ? _9 : false, fullWidth: true }));
    };
    const showField = (fieldName) => {
        let show = true;
        if (fieldName.requirements) {
            show = false;
            fieldName.requirements.forEach((requirement) => {
                let sourceHelper = getSourceHelpers(requirement.source);
                if (requirement.ids.some((id) => sourceHelper.type.includes(id))) {
                    show = true;
                }
            });
        }
        return show;
    };
    const renderSubForm = (sections, type) => {
        let header = _jsx(_Fragment, {});
        if (type && type === 'appointment') {
            let fields = 0;
            Object.values(sections).map((section, index) => (Object.values(section.fields).map((field, index) => {
                if (field.type === 'stack' && field.fields) {
                    Object.values(field.fields).map((innerfield, index) => {
                        if (showField(innerfield)) {
                            fields++;
                        }
                    });
                }
                else {
                    if (showField(field)) {
                        fields++;
                    }
                }
            })));
            if (fields === 0)
                return _jsx(_Fragment, {});
            header = _jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", sx: {
                            color: theme.palette.text.primary,
                            marginLeft: 1,
                            marginTop: 0,
                            fontSize: '1rem'
                        }, children: t('Vragen mbt jouw afspraak', 'summary') }), _jsx(Typography, { variant: "body2", sx: { textAlign: 'right', marginTop: '1em', fontStyle: 'italic' }, children: t('* verplicht veld', 'register') })] });
        }
        return (_jsxs(_Fragment, { children: [header, Object.values(sections).map((section, index) => (_jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(Typography, { variant: "h6", sx: {
                                marginLeft: 1,
                                marginTop: 2
                            }, children: t(section.label, 'register') }), _jsx(_Fragment, { children: Object.values(section.fields).map((field, index) => {
                                var _a, _b;
                                if (field.type === 'stack' && field.fields) {
                                    return (_jsx(Grid, { container: true, spacing: 1, children: Object.values(field.fields).map((innerfield, index) => {
                                            var _a, _b, _c, _d, _e, _f;
                                            if ((_a = innerfield.config) === null || _a === void 0 ? void 0 : _a.defaultValue) {
                                                formContext.setValue((field.fields ? Object.keys(field.fields)[index] : ''), (_b = innerfield.config) === null || _b === void 0 ? void 0 : _b.defaultValue);
                                            }
                                            return showField(innerfield) ? (_jsx(Grid, { item: true, xs: (_d = (_c = innerfield === null || innerfield === void 0 ? void 0 : innerfield.config) === null || _c === void 0 ? void 0 : _c.size) !== null && _d !== void 0 ? _d : Math.floor(12 / ((_f = Object.values((_e = field.fields) !== null && _e !== void 0 ? _e : []).length) !== null && _f !== void 0 ? _f : 1)), children: renderField(innerfield, field.fields ? Object.keys(field.fields)[index] : '') }, index)) : (_jsx(Fragment, {}, index));
                                        }) }, index));
                                }
                                if ((_a = field.config) === null || _a === void 0 ? void 0 : _a.defaultValue) {
                                    formContext.setValue(Object.keys(section.fields)[index], (_b = field.config) === null || _b === void 0 ? void 0 : _b.defaultValue);
                                }
                                return (_jsx(Fragment, { children: showField(field) && (_jsx(_Fragment, { children: renderField(field, Object.keys(section.fields)[index]) })) }, index));
                            }) }), _jsx(Divider, {})] }, index)))] }));
    };
    const setType = (event, value) => {
        // reset all forn state, like touched, dirty, etc, so the "new" form re-evaluates the config
        // particularly, to set the correct validations
        formContext.reset();
        // load the consumer values in the current form
        loadValues();
        // set the type to the new type
        formContext.setValue('type', value);
    };
    const renderForm = useMemo(() => {
        return (_jsxs(_Fragment, { children: [mode !== "summary" && (_jsxs(_Fragment, { children: [forms && forms.business && forms.consumer && !isLoginAccount && !consumer.hash && (_jsxs(Box, { sx: bookingCore ? {} : { paddingX: 1, marginTop: '2rem' }, children: [_jsx(FormLabel, { sx: bookingCore ? {} : {
                                        fontWeight: 700,
                                        fontSize: '1.5rem',
                                        color: theme.palette.text.primary
                                    }, children: t('Type klant', 'register') }), _jsxs(RadioGroup, { row: true, name: 'type', onChange: setType, children: [_jsx(FormControlLabel, { control: _jsx(Radio, { value: 'consumer', checked: type === 'consumer' }), label: t('Particulier', 'register') }), _jsx(FormControlLabel, { control: _jsx(Radio, { value: 'business', checked: type === 'business' }), label: t('Zakelijk', 'register') })] })] })), _jsx(Typography, { variant: "body2", sx: { textAlign: 'right', marginTop: '1em', fontStyle: 'italic' }, children: t('* verplicht veld', 'register') }), forms && forms.business && type === 'business' && (_jsx(_Fragment, { children: renderSubForm(forms.business) })), forms && forms.consumer && type === 'consumer' && (_jsx(_Fragment, { children: renderSubForm(forms.consumer) }))] })), mode === "summary" && (_jsxs(_Fragment, { children: [forms && forms.appointment && mode === 'summary' && (_jsx(_Fragment, { children: renderSubForm(forms.appointment, 'appointment') })), _jsx(Box, { children: showRemark && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", sx: bookingCore ? {} : {
                                            color: theme.palette.text.primary,
                                            marginLeft: 1,
                                            marginTop: 0,
                                            fontSize: '1rem'
                                        }, children: t('Voeg eventueel een opmerking toe', 'register') }), _jsx(CustomTextareaAutosizeElement, { required: false, label: t('Opmerking', 'register'), name: 'remark', rows: 3, sx: { my: 1 }, fullWidth: true })] })) })] }))] }));
    }, [forms.consumer, forms.business, consumer, type, mode]);
    const renderContracts = useMemo(() => {
        let hasCheckbox = contracts.filter((contract) => {
            return contract.checkbox !== false;
        }).length > 0, hasRequired = contracts.filter((contract) => {
            var _a;
            return contract.required !== false && !((_a = consumer.contracts) === null || _a === void 0 ? void 0 : _a.includes(contract.name));
        }).length > 0;
        if (!missingContracts && !isGuest) {
            // only for existing accounts, dont render this section if it doesnt have a required one
            return (_jsx(_Fragment, {}));
        }
        return (_jsx(_Fragment, { children: contracts && contracts.length > 0 && (_jsxs(_Fragment, { children: [contracts.map((contract, index) => {
                        var _a;
                        let label = t(contract.label, 'register'), link = label.match(/_(.+?)_/g);
                        if (link) {
                            let parts = label.toString().split(link[0]);
                            label = (_jsxs(_Fragment, { children: [_jsx("span", { children: parts[0] }), _jsx(Link, { to: contract.url, target: "_blank", children: link[0].replaceAll('_', '') }), _jsx("span", { children: parts[1] }), contract.required && (_jsx("span", { children: "*" }))] }));
                        }
                        else if (contract.required) {
                            label = label.concat('*');
                        }
                        if (contract.checkbox === false) {
                            return (_jsxs(Fragment, { children: [_jsx("input", Object.assign({}, formContext.register(contract.name, {}), { value: contract.value, type: "hidden" })), _jsx(Typography, { variant: "body1", sx: bookingCore ? {} : Object.assign({ color: theme.palette.text.primary, lineHeight: '40px', "& a": {
                                                color: theme.palette.text.primary,
                                            } }, (hasCheckbox && {
                                            marginLeft: '40px'
                                        })), children: label })] }, index));
                        }
                        return (_jsx(CustomCheckboxElement, { sx: {
                                marginLeft: 1
                            }, labelProps: bookingCore ? {} : {
                                sx: {
                                    color: theme.palette.text.primary,
                                    "& a": {
                                        color: theme.palette.text.primary,
                                    }
                                }
                            }, value: contract.value, name: contract.name, required: (_a = contract.required) !== null && _a !== void 0 ? _a : false, label: label }, index));
                    }), _jsx(Divider, {})] })) }));
    }, [contracts, consumer, missingContracts]);
    const submitLabel = mode === "accountDetails1"
        ? t('Gegevens opslaan', 'register')
        : ((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.actionLabel)
            // ? t((flow.currentRoute?.actionLabel ?? 'Volgende') as string)
            ? t('Volgende stap')
            : t('Volgende');
    return (_jsx(_Fragment, { children: _jsx(Box, { ref: formRef, children: _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, adapterLocale: language, localeText: localeText, children: _jsx(FormContainer, { formContext: formContext, onSuccess: onSubmit, onError: onError, children: _jsxs(FormErrorProvider, { onError: (error) => {
                            var _a;
                            if (error.type === 'validate') {
                                //  console.log(error)
                            }
                            if (error.type === 'custom') {
                                return t((_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : 'custom-error', 'error');
                            }
                            return t(error.message ? error.message : error.type, 'error');
                        }, children: [error && (_jsx(Alert, { id: errorsId, sx: Object.assign({ my: 2 }, (scrollErrorsIntoView ? { scrollMarginTop: '120px' } : {})), severity: "error", children: error === null || error === void 0 ? void 0 : error.map((_error, i) => (_jsx(Typography, { children: typeof _error === 'string' ? t(_error, 'register') : _error }, i))) })), renderForm, !bookingCore && mode === "register" && (_jsx(_Fragment, { children: hasAccounts && isGuest && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", sx: { marginLeft: 1 }, children: t('Maak een gebruikersaccount aan', 'register') }), _jsx(CustomTextFieldElement, { label: t('Gebruikersnaam', 'register'), name: 'username', required: registerRequired, fullWidth: true, disabled: consumer.hasUserAccount }), _jsx(PasswordField, { label: t('Wachtwoord', 'register'), disabled: consumer.hasUserAccount, required: registerRequired, name: "password" }), _jsx(Typography, { variant: "body2", sx: { marginLeft: 1 }, children: t('Indien je een wachtwoord invult kun je bij ons inloggen met je e-mail adres en dit wachtwoord om je afspraken in te zien en gemakkelijker vervolgafspraken te maken.', 'register') }), _jsx(Divider, {})] })) })), (["register", "accountDetails"].includes(mode) || missingContracts) && (_jsx(_Fragment, { children: renderContracts })), !bookingCore && !stickySubmit && (_jsx(Button, { type: "submit", disabled: !!disableSubmit, fullWidth: true, sx: { my: 1 }, variant: "contained", children: submitLabel })), !bookingCore && stickySubmit && (_jsx(BottomBar, { children: _jsx(Button, { type: "submit", disabled: !!disableSubmit, fullWidth: true, sx: {
                                        my: '16px'
                                    }, variant: "contained", children: submitLabel }) }))] }) }) }) }) }));
});
export default RegisterForm;
